import { useCallback, useEffect, useRef, useState } from "react";
import { errorText } from "./TabState";

// Cursor pages the way Students.js pages (044 D19): `fetchPage(cursor)` returns
// { rows, cursor, hasMore }; page N's cursor is page N−1's last document. `key`
// restarts from page 0 (a filter change). The newest load wins — a stale response
// never lands. `fetchPage` must be memoised by the caller (useCallback).
export const usePagedQuery = (fetchPage, key, fallback = "Failed to load.") => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const cursors = useRef({});
  const token = useRef(0);

  const load = useCallback(
    async (nextPage) => {
      const cursor = nextPage === 0 ? null : cursors.current[nextPage];
      if (nextPage !== 0 && !cursor) return; // a page never reached has no cursor
      const mine = ++token.current;
      setLoading(true);
      try {
        const data = await fetchPage(cursor);
        if (mine !== token.current) return;
        cursors.current[nextPage + 1] = data.cursor;
        setRows(data.rows);
        setHasMore(data.hasMore);
        setPage(nextPage);
        setError("");
      } catch (e) {
        if (mine !== token.current) return;
        console.error(e);
        setError(errorText(e, fallback));
      } finally {
        if (mine === token.current) setLoading(false);
      }
    },
    [fetchPage, fallback]
  );

  useEffect(() => {
    cursors.current = {};
    setRows([]);
    setPage(0);
    setHasMore(false);
    setError("");
    load(0);
  }, [load, key]);

  return { rows, page, hasMore, loading, error, load };
};
